/**
 * Centralized Realtime Provider
 *
 * This context manages a consolidated set of Supabase Realtime channels
 * to reduce database load. Instead of 10+ separate channels per component,
 * we use 2 shared channels:
 *
 * 1. transactional-data: inspections, defects, inspection_responses, defect_comments
 * 2. master-data: checklists, checklist_items, inspection_schedules, inspection_positions
 *
 * Components subscribe via useRealtimeSubscription hook which:
 * - Registers interest in specific tables
 * - Debounces callback invocations to prevent cascade updates
 * - Automatically cleans up on unmount
 */

import React, { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

// Tables grouped by update frequency
const TRANSACTIONAL_TABLES = [
  "inspections",
  "defects",
  "inspection_responses",
  "defect_comments",
  "notifications",
] as const;

const MASTER_DATA_TABLES = [
  "checklists",
  "checklist_items",
  "inspection_schedules",
  "inspection_positions",
  "teams_approval_requests",
  "tenant_memberships",
  "profiles",
  "user_roles",
  "teams",
  "team_members",
  // TES-1994: Gruppen-Zuordnungen (statisch + Resolver-materialisiert) — die Tasks-Gruppen-Queue
  // muss bei Mitgliedschafts-Änderungen sofort invalidieren (Publikation: Mig 370).
  "group_members",
  "group_members_effective",
  "machine_inspection_plan",
  "mpp_checklist",
  // TES-2032: Feature-Wave-Nachzug — bislang pure-pull abonnierte Tabellen. defect_action +
  // defect_task_links tragen den PT-TSK-Webhook-Status (fremder Server-Write), inspection_parameters
  // den Instanz-Override, team_group die Fachbereiche. Publikation: Mig 409 (machine_inspection_plan/
  // mpp_checklist waren registriert, aber unpubliziert = tote Subscription — 409 holt sie nach).
  "defect_action",
  "defect_task_links",
  "inspection_parameters",
  "team_group",
] as const;

type TransactionalTable = (typeof TRANSACTIONAL_TABLES)[number];
type MasterDataTable = (typeof MASTER_DATA_TABLES)[number];
type RealtimeTable = TransactionalTable | MasterDataTable;

interface Subscription {
  id: string;
  tables: RealtimeTable[];
  callback: () => void;
}

interface RealtimeContextValue {
  subscribe: (tables: RealtimeTable[], callback: () => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());
  const transactionalChannelRef = useRef<RealtimeChannel | null>(null);
  const masterDataChannelRef = useRef<RealtimeChannel | null>(null);
  const isInitializedRef = useRef(false);

  // Dispatch events to all matching subscribers
  const dispatchEvent = useCallback((table: string) => {
    subscriptionsRef.current.forEach((sub) => {
      if (sub.tables.includes(table as RealtimeTable)) {
        sub.callback();
      }
    });
  }, []);

  // Initialize channels once
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // TES-1123: realtime-js >=2.107 returns the EXISTING channel instance for a
    // reused topic and .on() throws on an already-subscribed channel. A unique
    // suffix avoids the StrictMode remount race against the async removeChannel
    // (postgres_changes does not depend on the client-side topic name).
    // Create transactional data channel (high-frequency updates)
    const transactionalChannel = supabase.channel(`realtime-transactional:${crypto.randomUUID()}`, {
      config: { broadcast: { self: false } },
    });

    // Tables that need DELETE events (hard-delete scenarios)
    const TABLES_WITH_DELETE = ["defects", "inspections"] as const;

    // Subscribe to INSERT and UPDATE events for all tables
    TRANSACTIONAL_TABLES.forEach((table) => {
      transactionalChannel.on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table },
        () => dispatchEvent(table),
      );
      transactionalChannel.on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table },
        () => dispatchEvent(table),
      );
      // DELETE for tables with hard-delete scenarios
      if (TABLES_WITH_DELETE.includes(table as (typeof TABLES_WITH_DELETE)[number])) {
        transactionalChannel.on(
          "postgres_changes",
          { event: "DELETE", schema: "public", table },
          () => dispatchEvent(table),
        );
      }
    });

    transactionalChannel.subscribe();
    transactionalChannelRef.current = transactionalChannel;

    // Create master data channel (low-frequency updates) — unique suffix, see above.
    const masterDataChannel = supabase.channel(`realtime-master-data:${crypto.randomUUID()}`, {
      config: { broadcast: { self: false } },
    });

    MASTER_DATA_TABLES.forEach((table) => {
      masterDataChannel.on("postgres_changes", { event: "INSERT", schema: "public", table }, () =>
        dispatchEvent(table),
      );
      masterDataChannel.on("postgres_changes", { event: "UPDATE", schema: "public", table }, () =>
        dispatchEvent(table),
      );
      masterDataChannel.on("postgres_changes", { event: "DELETE", schema: "public", table }, () =>
        dispatchEvent(table),
      );
    });

    masterDataChannel.subscribe();
    masterDataChannelRef.current = masterDataChannel;

    // Cleanup on unmount
    return () => {
      if (transactionalChannelRef.current) {
        supabase.removeChannel(transactionalChannelRef.current);
      }
      if (masterDataChannelRef.current) {
        supabase.removeChannel(masterDataChannelRef.current);
      }
      isInitializedRef.current = false;
    };
  }, [dispatchEvent]);

  // Subscribe function for components
  const subscribe = useCallback((tables: RealtimeTable[], callback: () => void): (() => void) => {
    const id = crypto.randomUUID();
    subscriptionsRef.current.set(id, { id, tables, callback });

    // Return unsubscribe function
    return () => {
      subscriptionsRef.current.delete(id);
    };
  }, []);

  return <RealtimeContext.Provider value={{ subscribe }}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeContext() {
  return useContext(RealtimeContext);
}

export type { RealtimeTable };
